import { useState } from "react";
import { ProductsComparisonProps } from "./productsComparisonModule";
import { ProductPricingResponse } from "../../types/coreApiMirror";
import { PricingLandingData } from "../../queries/pricingLandingQuery";
import { Colors } from "../../types/colors";
import {
  PricingFeatureGroup,
  Product,
  ProductFeatures,
} from "../../types/schema";
import { State } from "../../types/state";
import { getClassName } from "@pairtreefamily/utils";
import { priceFormatter } from "../../utils/priceFormatter";
import { LinkTo } from "../linkTo";
import { InfoIcon } from "../icons/info";
import Menu from "../menu";
import RoundedCheckMark from "../icons/roundedCheckMark";
import BestDealGraphic from "../icons/bestDealGraphic";
import { ButtonColor } from "../button";

export function ProductsComparisonMobileCard(props: {
  moduleInfo: Pick<PricingLandingData, "moduleProductsComparison">;
  selectedState?: State;
  productPriceLength: number;
  product: Product;
  monthPrice: ProductPricingResponse;
  yearPrice: ProductPricingResponse;
  oneTimePrice: ProductPricingResponse;
  isBestDeal: boolean;
}) {
  const moduleInfo = props.moduleInfo.moduleProductsComparison.moduleInput;
  const {
    monthPrice,
    yearPrice,
    oneTimePrice,
    product,
    productPriceLength,
    selectedState,
    isBestDeal,
  } = props;

  const colorMap = {
    darkblue: "text-darkblue",
    teal: "text-teal",
    green: "text-green",
    lightgreen: "text-lightgreen",
    peach: "text-peach",
    rust: "text-rust",
  };

  const colorBgMap = {
    darkblue: "bg-darkblue",
    teal: "bg-teal",
    green: "bg-green",
    lightgreen: "bg-lightgreen",
    peach: "bg-peach",
    rust: "bg-rust",
  };

  const colorBorderMap = {
    darkblue: "border-darkblue",
    teal: "border-teal",
    green: "border-green",
    lightgreen: "border-lightgreen",
    peach: "border-peach",
    rust: "border-rust",
  };

  const colorFilterMap = {
    darkblue: "filter-darkblue",
    teal: "filter-teal",
    green: "filter-green",
    lightgreen: "filter-lightgreen",
    peach: "filter-peach",
    rust: "filter-rust",
  };

  type PricingColor = Extract<
    Colors.Solid,
    "darkblue" | "teal" | "green" | "lightgreen" | "peach" | "rust"
  >;

  const [viewType, setViewType] = useState<"month" | "year">("month");

  const [expanded, setExpanded] = useState<boolean>(false);
  return (
    <>
      <div className="relative mb-8 mt-8 rounded-[15px] bg-[#F8F8F8] p-8 md:hidden">
        <div className={getClassName(!isBestDeal && "text-center")}>
          <p className="font-rebrand text-heading-20 text-black">
            <em>
              <strong>PairTree</strong>
            </em>
          </p>
          <h2 className="font-rebrand text-heading-30">
            <strong className="relative">
              {product?.name}
              {product?.name?.includes("plus") && (
                <img
                  className={getClassName(
                    "absolute right-0 ",
                    colorFilterMap[product?.color as PricingColor],
                  )}
                  src="/plus_graphic.svg"
                />
              )}
            </strong>
          </h2>
          {!selectedState && !productPriceLength && (
            <div>
              <p
                className={getClassName(
                  "text-body-2 mt-6",
                  colorMap[product?.color as PricingColor],
                )}
              >
                <strong>{moduleInfo.unselectedState?.status}</strong>
              </p>
              <p className="text-body-4 mt-2">
                <em>{moduleInfo.unselectedState?.description}</em>
              </p>
            </div>
          )}
          {selectedState && !productPriceLength && (
            <div>
              <p
                className={getClassName(
                  "text-body-2 mt-6",
                  colorMap[product?.color as PricingColor],
                )}
              >
                <strong>{moduleInfo.unavailableState?.status}</strong>
              </p>
              <p className="text-body-4 mt-2">
                <em>{moduleInfo.unavailableState?.description}</em>
              </p>
            </div>
          )}
          {oneTimePrice && productPriceLength && (
            <>
              <span
                className={getClassName(
                  "text-head-8",
                  colorMap[product?.color as PricingColor],
                )}
              >
                {priceFormatter(oneTimePrice.unit_amount / 100)}
              </span>
              <span className="text-body-3 ml-2">Pricing starts at</span>
            </>
          )}
          {(monthPrice || yearPrice) && productPriceLength && (
            <>
              {viewType === "month" && (
                <p>
                  <span
                    className={getClassName(
                      "text-head-8",
                      colorMap[product?.color as PricingColor],
                    )}
                  >
                    {priceFormatter(monthPrice.unit_amount / 100)}
                  </span>
                  <span className="text-body-3">&nbsp;/ month</span>
                </p>
              )}
              {viewType === "year" && (
                <p>
                  <span
                    className={getClassName(
                      "text-head-8",
                      colorMap[product?.color as PricingColor],
                    )}
                  >
                    {priceFormatter(yearPrice.unit_amount / 100)}
                  </span>
                  <span className="text-body-3">&nbsp;/ year</span>
                </p>
              )}
              <div className="text-body-4-semi cursor-pointer p-4">
                <div
                  className={getClassName(
                    "flex justify-around rounded-[80px] border",
                    colorBorderMap[product?.color as PricingColor],
                  )}
                >
                  <span
                    className={getClassName(
                      "w-full rounded-[80px] py-[2px]",
                      viewType === "month" &&
                        `${
                          colorBgMap[product?.color as PricingColor]
                        } text-white`,
                    )}
                    onClick={() => setViewType("month")}
                  >
                    Monthly
                  </span>
                  <span
                    className={getClassName(
                      "w-full rounded-[80px] py-[2px]",
                      viewType === "year" &&
                        `${
                          colorBgMap[product?.color as PricingColor]
                        } text-white`,
                    )}
                    onClick={() => setViewType("year")}
                  >
                    Yearly
                  </span>
                </div>
              </div>
            </>
          )}
          <div>
            {moduleInfo.footer?.ctaURL && (
              <div className="mt-6 flex justify-center">
                <LinkTo.External
                  className="w-full"
                  buttonStyle="primary"
                  style="button"
                  buttonColor={product?.color as ButtonColor}
                  url={moduleInfo.footer?.ctaURL}
                >
                  {moduleInfo.footer?.ctaText}
                </LinkTo.External>
              </div>
            )}
          </div>
          <button
            onClick={() => {
              setExpanded(!expanded);
            }}
            className="w-full"
          >
            <div className="mt-4 flex justify-center text-center">
              <div
                className={getClassName(
                  `border-${product?.color} w-[30px] rounded-full border-2 p-2`,
                  expanded && "scale-y-[-1]",
                )}
              >
                <span
                  className={getClassName(
                    `filter-${product?.color} flex justify-center`,
                  )}
                >
                  <img src="/down_arrow.svg" className={getClassName()} />
                </span>
              </div>
              <span
                className={getClassName(
                  `text-${product?.color} text-body-3 my-auto ml-2`,
                )}
              >
                <strong>{expanded ? "Close" : "Open"} full feature list</strong>
              </span>
            </div>
          </button>
        </div>
        {isBestDeal && product?.coreProductCode === "complete" && (
          <div className="absolute right-[-30px] top-[-33px]">
            <BestDealGraphic
              className={colorMap[product?.color as PricingColor]}
            />
          </div>
        )}

        {isBestDeal && product?.coreProductCode === "connect_plus" && (
          <div className="absolute right-[-30px] top-[-33px]">
            <BestDealGraphic
              className={colorMap[product?.color as PricingColor]}
            />
          </div>
        )}
      </div>
      {expanded && (
        <div>
          {moduleInfo.featureGroups?.map((group, index) => {
            const featuresFromGroup = moduleInfo.features.filter(
              (item) => item.group?._ref === group?._id,
            );
            // get only the features which apply to the current product
            const productFeatures = featuresFromGroup.filter((groupFeature) =>
              product.features?.find(
                (productFeature) =>
                  productFeature.feature?._ref === groupFeature._id,
              ),
            );
            if (productFeatures.length === 0) {
              return null;
            }
            return (
              <div key={group._id} className="text-left">
                <h3 className="text-head-7 mt-8 border-b-4 border-[#F2F2F2] pb-4">
                  {group.name}
                </h3>
                {productFeatures.map((feature) => (
                  <Feature
                    key={`${group._id}-${feature._id}`}
                    group={group}
                    feature={feature}
                    product={product}
                  />
                ))}
              </div>
            );
          })}
          <div className="text-center">
            <p
              className={getClassName(
                "text-body-2 mt-6",
                colorMap[product?.color as PricingColor],
              )}
            >
              <strong>{moduleInfo.footer?.line1}</strong>
            </p>
            <p className="text-body-2 mb-4">
              <em>{moduleInfo.footer?.line2}</em>
            </p>
            {moduleInfo.footer?.ctaURL && (
              <div className="mb-8 flex justify-center">
                <LinkTo.External
                  className="px-16"
                  buttonColor={product?.color as ButtonColor}
                  url={moduleInfo.footer.ctaURL}
                  buttonStyle="primary"
                  style="button"
                >
                  {moduleInfo.footer.ctaText}
                </LinkTo.External>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Feature(props: {
  group: PricingFeatureGroup;
  feature: ProductFeatures;
  product: Product;
}) {
  const colorMap = {
    darkblue: "text-darkblue",
    teal: "text-teal",
    green: "text-green",
    lightgreen: "text-lightgreen",
    peach: "text-peach",
    rust: "text-rust",
  };

  type PricingColor = Extract<
    Colors.Solid,
    "darkblue" | "teal" | "green" | "lightgreen" | "peach" | "rust"
  >;
  const { group, feature, product } = props;
  return (
    <div
      key={`${group._id}-${feature._id}`}
      className="flex justify-between border-b-[2px] border-[#f2f2f2] py-4"
    >
      <div className="flex gap-4">
        <span className="text-body-3 text-black">
          <strong>{feature.name}</strong>
        </span>
        <div className="my-auto">
          <Menu
            tooltipBgColor="darkblue"
            openOnHover={true}
            className="!max-w-[250px]"
            id="tooltip-selector"
            elementTrigger={(isOpen) => (
              <>
                {isOpen && <InfoIcon type="enabled" fillColor="green" />}
                {!isOpen && <InfoIcon type="disabled" />}
              </>
            )}
          >
            <div className="w-full">
              <p className="text-body-4 p-4 text-white">{feature.tooltip}</p>
            </div>
          </Menu>
        </div>
      </div>
      <div className="flex gap-4">
        <span
          className={getClassName(
            "flex justify-center",
            colorMap[product?.color as PricingColor],
          )}
        >
          <RoundedCheckMark />
        </span>
      </div>
    </div>
  );
}
